
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const whatsappMessage = encodeURIComponent("Hello 👋 The Tooth Doctor,\nI would like to book an appointment / make an enquiry about your dental services.\nPlease share available slots and details.");
  const whatsappUrl = `https://wa.me/916369090441?text=${whatsappMessage}`;

  const services = [
    'General Check-up',
    'Dental Implants',
    'Root Canal Treatment',
    'Cosmetic Dentistry',
    'Orthodontics',
    'Teeth Whitening',
    'Pediatric Dentistry',
    'Emergency Care'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3hp3ttsoafcrsrcptu0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div className="order-2 lg:order-1">
            <div className="mb-6 sm:mb-8">
              <span className="text-teal-400 font-semibold text-sm uppercase tracking-wide">Contact Us</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-2 mb-4 sm:mb-6">Get in Touch</h2>
              <p className="text-lg sm:text-xl text-gray-300">
                Ready to transform your smile? Contact us today to schedule your appointment or learn more about our services.
              </p>
            </div>

            <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-base sm:text-lg mb-1">Visit Our Clinic</h4>
                  <p className="text-gray-300 text-sm sm:text-base">The Tooth Doctor - Advanced Dental Care Centre</p>
                  <p className="text-gray-300 text-sm sm:text-base">No.9/3, First street, Thambu Swamy Nagar</p>
                  <p className="text-gray-300 text-sm sm:text-base">Near Bus Stand, Thiruverkadu, Chennai</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-base sm:text-lg mb-1">Call Us</h4>
                  <p className="text-gray-300 text-sm sm:text-base">
                    <a href="tel:+916369090441" className="hover:text-white transition-colors">+91 6369090441</a>
                  </p>
                  <p className="text-gray-300 text-sm sm:text-base">
                    <a href="tel:+919884885014" className="hover:text-white transition-colors">+91 9884885014</a>
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-base sm:text-lg mb-1">Email Us</h4>
                  <p className="text-gray-300 text-sm sm:text-base">
                    <a href="mailto:toothdoctor.in@gmail.com" className="hover:text-white transition-colors">
                      toothdoctor.in@gmail.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                variant="secondary" 
                size="lg"
                onClick={() => window.open(whatsappUrl, '_blank')}
                className="w-full sm:flex-1"
              >
                <i className="ri-calendar-line mr-2"></i>
                Book Appointment
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:flex-1 border-white text-white hover:bg-white hover:text-gray-900">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full">
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-gray-900 order-1 lg:order-2">
            <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Send us a Message</h3>
            
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div className="sm:col-span-2 sm:grid sm:grid-cols-2 sm:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter your email address"
                />
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Interested In
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your dental needs or any questions you have..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-4 sm:mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
                  Thank you! Your message has been sent successfully. We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-4 sm:mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                  Sorry, there was an error sending your message. Please try again or call us directly.
                </div>
              )}

              <Button 
                type="submit" 
                variant="primary" 
                size="lg" 
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
